import type { RecurringSchedule } from './recurring-schedule-module';
import { getSchedules } from './recurring-schedule-module';
import { safeFormatDate } from '../../utils/format-dates';
import type { PriceCategory, V2UserMessage } from '../../types/api-v2.types';
import type { ThunkAction, APIError, AppState } from '../../types/app.types';
import type { Quantity } from './drugs.types';
import type { ConsultActionItems } from './subscriptions-module';

const DATE_FORMAT = 'MMMM D, YYYY';
const MAGIC = 'magic';
export const PICKUP_PRICE_CATEGORY = 'edlp';
export const BSD_PRICE_CATEGORY = 'local';
export type PendingMed = {
  slug: string;
  medId: string;
  quantity: number;
  description: string;
  dispenseAsWritten: boolean;
  pharmaPartner: boolean;
};
export type OrderExceptionBlock = {
  addressInfo: Array<string> | null | undefined;
  cxInfo:
    | {
        phoneNumber: string;
        text: string;
      }
    | null
    | undefined;
  ePrescribeInfo: Array<string> | null | undefined;
  message: string;
};
export type DeliveryAddress = {
  city: string;
  state: string;
  street: string;
  zipCode: string;
};
export type DeliveryInfo = {
  address: DeliveryAddress;
  deliveryOrderMedDisplayStatus: string;
  estDelivery: string;
  exception: OrderExceptionBlock | null | undefined;
  shipper: string | null | undefined;
  status: string;
  trackingNumber: string | null | undefined;
  trackingUrl: string | null | undefined;
};
export type PrescriptionPharmacy = {
  name: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
};
type BaseMyMed = {
  deliveryInfo: DeliveryInfo | null | undefined;
  formattedName: string;
  medId: string;
  medNameId: string;
  orderId: string | null | undefined;
  orderItemId: string | null | undefined;
  orderStatus: string | null | undefined;
  prescription:
    | {
        expirationDate: string | null | undefined;
        lastFilled: string | null | undefined;
        rxExpiration: string | null | undefined;
        prescribingDoctor: string;
        pharmacyName: string | null | undefined;
        pharmacy: PrescriptionPharmacy | null | undefined;
        quantity: string;
        refillsRemaining: string;
      }
    | null
    | undefined;
  priceCategory: PriceCategory;
  sharedAccountsConsultationLink: string | null | undefined;
  slug: string;
  userMessage: V2UserMessage | null | undefined;
  consultActionItem: ConsultActionItems | null | undefined;
  subsRecommendation: boolean | null | undefined;
  condition: string;
};
type MyMedAPIItem = BaseMyMed & {
  drug: Quantity | null | undefined;
  pinned: boolean;
  isMagic: boolean;
  isUpForMagicRefill: boolean | null | undefined;
};
export type MedStatusIcons = 'PharmacyFulfillment' | 'ExclamationTriangle';
type MedStatus = {
  status: string;
  color: string;
  header: {
    text: string;
    icon: {
      key: MedStatusIcons;
      color: string;
      background: string;
    };
  };
  content: {
    text: string;
  };
  ctas: Array<{
    text: string;
    type: 'call_pharmacy';
  }>;
};
export type MyMedItem = BaseMyMed & {
  drug: Quantity;
  pinned: boolean;
  isMagic: boolean;
  isTelemedEligible: boolean;
  isUpForMagicRefill: boolean | null | undefined;
  medStatus: MedStatus | null | undefined;
};
type GetMedsSuccess = {
  type: 'my_meds/MEDICATIONS_SUCCESS';
  data: Array<MyMedAPIItem>;
};
type GetMedsError = {
  type: 'my_meds/MEDICATIONS_ERROR';
} & APIError;
type PinMedSuccess = {
  type: 'my_meds/PIN_MED_SUCCESS';
  data: string;
};
type UnPinMedSuccess = {
  type: 'my_meds/UNPIN_MED_SUCCESS';
  data: string;
};
type SetPendingMeds = {
  type: 'my_meds/SET_PENDING_MEDS';
  data: Array<PendingMed>;
};
type ClearPendingMeds = {
  type: 'my_meds/CLEAR_PENDING_MEDS';
};
export type MyMedsAction =
  | GetMedsSuccess
  | GetMedsError
  | PinMedSuccess
  | UnPinMedSuccess
  | SetPendingMeds
  | ClearPendingMeds;
export const findAvailableMagicPrice = (med: MyMedItem) => {
  return med.drug.price && med.drug.price[MAGIC];
};
export const isHDPriceTierAvailable = (med: MyMedItem) => {
  if (med.drug && med.drug.price && med.drug.price.delivery) {
    const { rawValue } = med.drug.price.delivery;
    if (rawValue || rawValue === 0) return true;
  }

  return false;
};

export function getMyMeds(): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .get(`/v2/user/my_meds`)
      .then((res) => res.data)
      .then((response: { result: Array<MyMedAPIItem> }) =>
        dispatch({
          type: 'my_meds/MEDICATIONS_SUCCESS',
          data: response.result,
        })
      )
      .then(() => dispatch(getSchedules()))
      .catch((err: APIError) =>
        dispatch({
          type: 'my_meds/MEDICATIONS_ERROR',
          ...err,
        })
      );
  };
}

export function setPendingMeds(meds: Array<PendingMed>): SetPendingMeds {
  return {
    type: 'my_meds/SET_PENDING_MEDS',
    data: meds,
  };
}

export function clearPendingMeds(): ClearPendingMeds {
  return {
    type: 'my_meds/CLEAR_PENDING_MEDS',
  };
}

export function pinMed(slug: string, medId: string, quantity: number): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .put(`/v2/user/drugs/detail/${slug}/dosage/${medId}/quantity/${quantity}`, {
        priceCategory: 'edlp',
        pinned: true,
      })
      .then((res) => res.data)
      .then(() =>
        dispatch({
          type: 'my_meds/PIN_MED_SUCCESS',
          data: slug,
        })
      )
      .catch((err: APIError) => {
        dispatch({
          type: 'my_meds/MEDICATIONS_ERROR',
          ...err,
        });
        throw err;
      });
  };
}

/*Returns an array of *un-pinnable* medications if pinMed call is unsuccessful
  Medications may need action required if:
    Med is dispense as written
    Med is a pharma partner medication
    FDQ mismatch in ecom backend
*/
export function pinOrganicReferrals(pendingMeds: Array<PendingMed>): ThunkAction<Promise<any>> {
  return function (dispatch): Promise<any> {
    const actionRequiredMeds = [];

    const attemptPin = async (med) => {
      /* Normalizing med_name(s) (via rxOS) to slug(s)
        1) Removes anything after an opening paren is found
        2) Removes trailing whitespace
        3) Replace remaining spaces with dashes
      */
      let slug = med.slug;
      slug = slug.replace(/\(.*/g, '');
      slug = slug.trim();
      slug = slug.replace(/\s/g, '-');

      if (med.dispenseAsWritten || med.pharmaPartner) {
        return actionRequiredMeds.push(slug);
      } else {
        return await dispatch(pinMed(slug, med.medId, med.quantity))
          .then(() => Promise.resolve(false))
          .catch(() => {
            return actionRequiredMeds.push(slug);
          });
      }
    };

    pendingMeds.forEach((m) => attemptPin(m));
    return Promise.all(actionRequiredMeds);
  };
}

export function unpinMed(slug: string, medId: string, quantity: number): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .put(`/v2/user/drugs/detail/${slug}/dosage/${medId}/quantity/${quantity}`, {
        priceCategory: 'edlp',
        pinned: false,
      })
      .then((res) => res.data)
      .then(() =>
        dispatch({
          type: 'my_meds/UNPIN_MED_SUCCESS',
          data: slug,
        })
      )
      .catch((err: APIError) =>
        dispatch({
          type: 'my_meds/MEDICATIONS_ERROR',
          ...err,
        })
      );
  };
}

export const getMedFromState = (state: AppState, orderMedId: string): MyMedItem | null | undefined =>
  state.myMeds.meds ? state.myMeds.meds.find((med) => med.medId === orderMedId) : null;
export const hasMedications = (state: AppState) => !!(state.myMeds.meds && state.myMeds.meds.length > 0);
const ActiveMedStatuses = ['pending', 'transferring', 'dispensing', 'shipping', 'active'];
export const isMedActive = (med: MyMedItem) => ActiveMedStatuses.includes(med.orderStatus);
export const canAddToCart = (med: MyMedItem, schedule: RecurringSchedule | null | undefined) =>
  med && !schedule && !isMedActive(med);
export const isAwaiting = (status: string) => status && status.match(/pending|transferring/g);
export const isActiveRetailStatus = (status: string | null | undefined) => status && status === 'active';
export const isCancelable = (med: MyMedItem) => {
  if (med.medStatus && med.medStatus.status === 'ready_for_pickup') {
    return false;
  }

  return (
    (med.deliveryInfo && isAwaiting(med.deliveryInfo.status)) ||
    (!med.deliveryInfo && isActiveRetailStatus(med.orderStatus))
  );
};
export type MyMedsState = {
  meds: Array<MyMedItem> | null | undefined;
  pending: Array<PendingMed> | null | undefined;
};
export default function myMedsReducer(
  state: MyMedsState = {
    meds: null,
    pending: null,
  },
  action: any
): MyMedsState {
  switch (action.type) {
    case 'my_meds/MEDICATIONS_SUCCESS':
      return {
        ...state,
        meds: action.data
          .filter((med) => med && med.drug && med.pinned)
          .map((med) => ({
            ...med,
            prescription: med.prescription
              ? {
                  ...med.prescription,
                  lastFilled: safeFormatDate(med.prescription.lastFilled, DATE_FORMAT),
                  expirationDate: safeFormatDate(med.prescription.expirationDate, DATE_FORMAT),
                }
              : null,
          })),
      };

    case 'orders/CANCEL_SUCCESS': {
      if (!state.meds) return state;
      const nextState = { ...state };
      const cancelledMed = nextState.meds && nextState.meds.find((myMed) => myMed.orderItemId === action.data.id);

      if (cancelledMed) {
        cancelledMed.orderId = null;
        cancelledMed.orderItemId = null;
        cancelledMed.orderStatus = null;
      }

      return nextState;
    }

    case 'my_meds/PIN_MED_SUCCESS':
      return { ...state, pending: state.pending && state.pending.filter((p) => p.slug !== action.data) };

    case 'my_meds/UNPIN_MED_SUCCESS':
      return {
        meds: state.meds && state.meds.filter((m) => m.slug !== action.data),
        pending: state.pending && state.pending.filter((p) => p.slug !== action.data),
      };

    case 'my_meds/SET_PENDING_MEDS':
      return {
        ...state,
        pending: action.data.filter((p) => !(state.meds && state.meds.findIndex((m) => m.medId === p.medId) > 0)),
      };

    case 'my_meds/CLEAR_PENDING_MEDS':
      return { ...state, pending: null };

    default:
      return state;
  }
}
